import React, { memo, useMemo } from 'react';
import { Button, Tooltip } from 'antd';
import moment from 'moment';
import { AssetItemDto, AudioVideoMetadataDto } from '@api/Api';
import Status from '@components/Approval/Status';
import Loader from '@components/Loader';
import { assetPreviewThumbnails } from '@helpers/assetPreviewThumbnails';
import { formatDuration } from '@helpers/formatDuration';
import { ReactComponent as DownloadInvoiceSvg } from '@assets/icons/download-invoice.svg';
import { download } from '@helpers/download';

type TilesViewItemProps = {
  asset: AssetItemDto;
  onOpenAsset: (asset: AssetItemDto) => void;
};

export default memo(function TilesViewItem({
  asset,
  onOpenAsset
}: TilesViewItemProps) {
  const version = useMemo(() => {
    return asset.asset?.versions[0];
  }, [asset.asset?.versions]);

  const isAudioOrVideo = useMemo(() => {
    return (
      version?.metadata.type === 'audio' || version?.metadata.type === 'video'
    );
  }, [version]);

  const durationTag = useMemo(() => {
    if (!isAudioOrVideo) return;
    const meta = version?.metadata as AudioVideoMetadataDto;
    return formatDuration(
      meta.info.avInfo?.audio?.durationSeconds ||
        meta.info.avInfo?.video?.durationSeconds ||
        0
    );
  }, [version, isAudioOrVideo]);

  const preparePreview = useMemo(() => {
    const res = assetPreviewThumbnails({
      url: version?.metadata.preview?.url || '',
      status: version?.metadata.preview?.status || '',
      type: version?.metadata.type || '',
      format: 'img'
    });

    if (res === 'pending') return 'pending';
    if (typeof res === 'string') return <img src={res || ''} alt="" />;
    return res;
  }, [version]);

  return (
    <div className="asset-item-wrapper" onClick={() => onOpenAsset(asset)}>
      <div className="asset-item-wrapper__preview">
        {preparePreview === 'pending' && (
          <Loader
            classNameWrapper="assets-loader-wrapper"
            spinSize={40}
            isSpinning={true}
          />
        )}
        <div>{preparePreview !== 'pending' && preparePreview}</div>
        {isAudioOrVideo && (
          <div className="asset-item-wrapper__preview--time-tag">
            {durationTag}
          </div>
        )}
        {!!asset.asset?.approvalWorkflow && (
          <Status
            showOverdueTag
            status={asset.asset.approvalWorkflow.status}
            approvalWorkflowStatus={asset.asset.approvalWorkflow.status}
            approvalWorkflowIsPaused={asset.asset.approvalWorkflow.isPaused}
            deadline={asset.asset.approvalWorkflow.deadline}
          />
        )}
      </div>
      <div className="asset-item-wrapper-info">
        <div className="asset-item-wrapper-info__top">
          <Tooltip
            title={`${version?.name}.${version?.extension}`}
            overlayClassName="toggle_favorite_overlay toggle_favorite_overlay--small"
          >
            <div className="asset-item-wrapper-info__title">
              {version?.name}.{version?.extension}
            </div>
          </Tooltip>
          <Button
            type="text"
            size="small"
            className="campaign-review-btn-download"
            disabled={!asset.asset?.permissions.download}
            icon={<DownloadInvoiceSvg />}
            onClick={(e) => {
              e.stopPropagation();
              if (!asset.asset) return;
              const { name, sourceUrl } = asset.asset.versions[0];
              download(sourceUrl, name);
            }}
          />
        </div>
        <div className="asset-item-wrapper-info__description">
          Edited {moment(asset.modifiedAt).fromNow()}
        </div>
      </div>
    </div>
  );
});
